import React, { useRef } from "react";
import CopyImageChip from "./CopyImageChip";
import { buildWildcardRace } from "./cupStats";

/**
 * Bar-chart panel ranking every non-group-winner by group-stage points,
 * with the wildcard cutoff highlighted. Hidden when the cup format has no
 * wildcard spots or no scores exist yet.
 */
const WildcardRacePanel = ({ cup }) => {
    const ref = useRef(null);
    const race = buildWildcardRace(cup);
    if (!race) return null;

    const isFinal = cup.bracket?.length > 0;
    const spotLabel = race.wildcardCount === 1 ? "spot" : "spots";

    return (
        <div
            ref={ref}
            className="overflow-hidden rounded-2xl border border-border bg-surface shadow-card"
        >
            <div className="flex items-center justify-between border-b-[1.5px] border-[#e7e2d5] px-[18px] py-3.5">
                <h3 className="font-display text-[19px] font-black text-ds-text">
                    Wildcard Race
                </h3>
                <CopyImageChip targetRef={ref} fileName="league-cup-wildcard-race" />
            </div>
            <div className="grid items-center md:grid-cols-[1.5fr,1fr]">
                <div className="px-5 py-5">
                    {race.entries.map((entry) => (
                        <div
                            key={entry.team_name}
                            className="mb-3.5 flex items-center gap-2.5 last:mb-0"
                        >
                            <span className="w-[168px] truncate text-xs font-sans font-extrabold text-text-mid">
                                {entry.owner_name || entry.team_name}
                            </span>
                            <div className="h-[18px] flex-1 overflow-hidden rounded-md bg-[#ece7da]">
                                <div
                                    className={`h-full rounded-md ${
                                        entry.isWildcard
                                            ? "bg-gradient-to-r from-mustard to-[#e3b52e]"
                                            : "bg-border"
                                    }`}
                                    style={{ width: `${entry.width}%` }}
                                />
                            </div>
                            <span className="w-[52px] text-right text-xs font-sans font-extrabold tabular-nums text-text-mid">
                                {entry.points.toFixed(2)}
                            </span>
                        </div>
                    ))}
                </div>
                <div className="flex flex-col justify-center border-t-[1.5px] border-[#e7e2d5] px-6 py-5 md:border-l-[1.5px] md:border-t-0">
                    {race.margin != null && (
                        <div className="font-display text-[30px] font-black text-forest">
                            +{race.margin.toFixed(2)}
                        </div>
                    )}
                    <p className="mt-2 text-[12.5px] font-sans font-semibold leading-relaxed text-text-muted">
                        {race.wildcardCount} knockout {spotLabel} went to the best
                        non-winner{race.wildcardCount === 1 ? "" : "s"} across all
                        groups.{" "}
                        {isFinal ? (
                            <>
                                <b className="text-forest">
                                    {(() => {
                                        const wc = race.entries.find((e) => e.isWildcard);
                                        return wc ? wc.owner_name || wc.team_name : "";
                                    })()}
                                </b>{" "}
                                claimed {race.wildcardCount === 1 ? "it" : "one"}
                                {race.margin != null
                                    ? ` by ${race.margin.toFixed(2)} points`
                                    : ""}
                                .
                            </>
                        ) : (
                            "Standings update as each Cup week completes."
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default WildcardRacePanel;
